import { createSlice } from "@reduxjs/toolkit";

import { fetchGetCompanies, fetchSessionCompanies } from "./actions";
import { DataStatus } from "../../types";

type Company = {
  url: string;
  id: string;
  name: string;
  shortName: string;
  domainUrl: string;
};

type CompaniesState = {
  data: Array<Company>;
  status: DataStatus;
  // The nav bar's own lifecycle (DEV-5516), kept apart from `status` so that a background
  // name lookup neither satisfies a page's `getIsCompaniesLoaded` gate nor overwrites the
  // error state a page is showing. Optional: a state built before this fetch existed is
  // simply one that has not run it.
  sessionStatus?: DataStatus;
  error: string | null;
};

/**
 * The loaded companies with `fetched` layered on top of them, matched by id.
 *
 * The session fetch (DEV-5516) reads the same scoped list as the page fetches, so this is
 * normally a replacement; merging keeps a company a later, narrower response omits rather
 * than dropping a name the nav bar is already showing. Page fetches still replace, so a
 * company removed server-side leaves the store the next time a page loads the list.
 */
const mergeCompanies = (
  current: Array<Company>,
  fetched: unknown
): Array<Company> => {
  if (!Array.isArray(fetched)) {
    return current;
  }
  const merged = [...current];
  fetched.forEach((company: Company) => {
    const index = merged.findIndex(({ id }) => id === company.id);
    if (index === -1) {
      merged.push(company);
    } else {
      merged[index] = { ...merged[index], ...company };
    }
  });
  return merged;
};

const initialState: CompaniesState = {
  data: [],
  status: DataStatus.idle,
  sessionStatus: DataStatus.idle,
  error: null,
};

export const Slice = createSlice({
  name: "companies",
  initialState,
  reducers: {
    resetCompanies: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGetCompanies.pending, (state) => {
        state.status = DataStatus.loading;
        state.error = null;
      })
      .addCase(fetchGetCompanies.fulfilled, (state, action) => {
        state.status = DataStatus.succeeded;
        state.data = action.payload;
      })
      .addCase(fetchGetCompanies.rejected, (state, action) => {
        state.status = DataStatus.failed;
        state.error = action.error.message ?? "";
      })
      .addCase(fetchSessionCompanies.pending, (state) => {
        state.sessionStatus = DataStatus.loading;
      })
      .addCase(fetchSessionCompanies.fulfilled, (state, action) => {
        state.sessionStatus = DataStatus.succeeded;
        state.data = mergeCompanies(state.data, action.payload);
      })
      .addCase(fetchSessionCompanies.rejected, (state) => {
        // Resolving names is best effort: leave the store as if the nav bar had never
        // asked, so the UUID fallback renders and a later mount can try again.
        state.sessionStatus = DataStatus.idle;
      });
  },
});

export default Slice;
